/** Everything that depends on balances, and the mutation helper that refreshes it. */
import {
  useMutation,
  useQueryClient,
  type QueryClient,
  type QueryKey,
  type UseMutationOptions,
} from "@tanstack/react-query";

/** Everything that depends on transactions / balances. */
const MONEY_KEYS: QueryKey[] = [
  ["dashboard"],
  ["accounts"],
  ["cards"],
  ["transactions"],
  ["budget"],
  ["calendar"],
  ["charts"],
  ["affordability"],
  ["debts"],
  ["pot"],
  ["payments"],
  ["plan"],
  ["msi"],
  // an MSI purchase's own budget item follows it
  ["budget-items"],
];

/**
 * Mark every money query stale. Only the ones on screen refetch now (`refetchType: "active"`);
 * the rest refetch when their screen opens. `skip` leaves keys alone that the change can't touch.
 */
export function invalidateMoney(qc: QueryClient, skip: QueryKey[] = []): Promise<void> {
  const keep = new Set(skip.map((k) => JSON.stringify(k)));
  return Promise.all(
    MONEY_KEYS.filter((k) => !keep.has(JSON.stringify(k))).map((queryKey) =>
      qc.invalidateQueries({ queryKey, refetchType: "active" }),
    ),
  ).then(() => undefined);
}

export function useInvalidateMoney(): () => Promise<void> {
  const qc = useQueryClient();
  return () => invalidateMoney(qc);
}

/** A mutation that changes balances. Money views refresh in the background: the caller (a
 * sheet closing, a toast) doesn't wait for the refetches. */
export function useMoneyMutation<TData, TVars>(
  fn: (vars: TVars) => Promise<TData>,
  options?: Omit<UseMutationOptions<TData, Error, TVars>, "mutationFn">,
) {
  const qc = useQueryClient();
  return useMutation<TData, Error, TVars>({
    mutationFn: fn,
    ...options,
    onSuccess: async (data, vars, ctx, mctx) => {
      void invalidateMoney(qc);
      await options?.onSuccess?.(data, vars, ctx, mctx);
    },
  });
}
